import { SelectChangeEvent, Divider, MenuItem, Avatar } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { BsTable } from 'react-icons/bs';
import { FaChevronUp } from 'react-icons/fa';
import { FcLowPriority, FcMediumPriority, FcHighPriority } from 'react-icons/fc';
import { GrTask } from 'react-icons/gr';
import { IoChatbubblesOutline } from 'react-icons/io5';
import Chat from 'src/components/chat/Chat';
import { updateActionPlan, updateCommentActionPlan } from 'src/reducers/account.reducer';
import { setErrors, setLoading } from 'src/reducers/app.reducer';
import { addEventTracking, emphasizedText, getResponsibleName, stringAvatar } from 'src/utils/utils';
import { actionsUrgencyComplexity } from '../constants';
import ActionStatus from './ActionStatus';
import { DivContainer, Tag, CustomSelect, CustomDatePicker, ResponsibleContainer, ResponsibleItem, TabsContainer, Tab, Badge } from './Table.styles';
import { useDispatch } from 'react-redux';
import { ActionProps, EntityProps } from '../types';
import Drawer from 'src/components/drawer';
import { Button } from 'src/components/Button';
import { MdClose } from 'react-icons/md';

interface UserProps {
  id: string;
  name: string;
  firstName: string;
  email: string;
}

interface ActionDetailsProps {
  selectedAction: ActionProps;
  activeAccount: any;
  selectedEntity: EntityProps;
  showDrawer: boolean;
  setShowDrawer: React.Dispatch<React.SetStateAction<boolean>>;
  setSelectedAction: React.Dispatch<React.SetStateAction<ActionProps>>;
  selectedDrawerItemID: string | null;
  currentUser: any;
  admin: boolean;
  editedAction: ActionProps;
  setShowSubcontractorInputModal: React.Dispatch<React.SetStateAction<boolean>>;
  isAnyNewComment: (action: any) => boolean;
  impersonated: any;
}
const ActionDetails: React.FC<ActionDetailsProps> = ({
  selectedAction,
  activeAccount,
  setSelectedAction,
  selectedEntity,
  showDrawer,
  setShowDrawer,
  selectedDrawerItemID,
  currentUser,
  admin,
  editedAction,
  setShowSubcontractorInputModal,
  isAnyNewComment,
  impersonated,
}) => {
  const dispatch = useDispatch<any>();
  const [selectedItemID, setSelectedItemID] = useState<string | null>(selectedDrawerItemID);

  useEffect(() => {
    setSelectedItemID(selectedDrawerItemID);
  }, [selectedDrawerItemID]);

  const datePickerRef = useRef(null);

  const comments = useMemo(() => selectedAction.comment ?? [], [selectedAction.comment]);

  const isActionChanged = (selectedAction, editedAction) => {
    return JSON.stringify(selectedAction) !== JSON.stringify(editedAction);
  };

  const handleValidateEditedAction = () => {
    dispatch(setLoading(true));
    addEventTracking('Action Plan', 'Update Action');
    dispatch(updateActionPlan({ accountId: activeAccount._id, entityId: selectedEntity._id, actions: selectedAction }));
    setSelectedAction({});
    setShowDrawer(prevState => !prevState);
    dispatch(setLoading(false));
  };
  const handleDateChange = date => {
    setSelectedAction(prevState => ({ ...prevState, dueDate: dayjs(date).format('DD-MM-YYYY') }));
  };

  const handleStatusChange = (event: SelectChangeEvent) => {
    setSelectedAction(prevState => ({ ...prevState, status: event.target.value }));
  };

  const updateCommentAction = message => {
    let updatedAction = {
      ...selectedAction,
      comment: [...selectedAction.comment, message],
    };

    setSelectedAction(updatedAction);

    let sharedItem = {
      entityId: selectedEntity._id,
      shareId:
        'entity/' +
        selectedEntity._id +
        '/product/' +
        selectedAction.relatedProductId +
        '/action/' +
        selectedAction.id +
        '/version/' +
        selectedAction.versionProductId,
      actionId: selectedAction.id,
      relatedProductId: selectedAction.relatedProductId,
      versionProductId: selectedAction.versionProductId,
      message: true,
    };

    dispatch(
      updateCommentActionPlan({
        accountId: activeAccount._id,
        entityId: selectedEntity._id,
        actionId: selectedAction.id,
        comment: message,
        host: currentUser,
        sharedItem: sharedItem,
      })
    );
  };

  const handleDateClick = event => {
    const target = event.target;
    if (target.classList.contains('MuiInputBase-input')) {
      datePickerRef.current.querySelector('button').click();
    }
  };

  const updateSeenStatus = () => {
    if (isAnyNewComment(selectedAction) && Array.isArray(selectedAction.comment) && selectedAction.comment.length > 0) {
      const updatedAction = {
        ...selectedAction,
        comment: selectedAction.comment.map(message => {
          const currentUserSeenIndex = message.seenComment.findIndex(seen => seen.userID === currentUser._id);
          if (currentUserSeenIndex === -1 || !message.seenComment[currentUserSeenIndex].seen) {
            const updatedSeenComment =
              currentUserSeenIndex === -1
                ? [...message.seenComment, { userID: currentUser._id, seen: true }]
                : message.seenComment.map(seen => (seen.userID === currentUser._id ? { ...seen, seen: true } : seen));
            return { ...message, seenComment: updatedSeenComment };
          }
          return message;
        }),
      };

      setSelectedAction(updatedAction);
      dispatch(updateActionPlan({ accountId: activeAccount._id, entityId: selectedEntity._id, actions: updatedAction }));
    }
  };

  const deleteComment = filteredComments => {
    const newSelectedAction = {
      ...selectedAction,
      comment: filteredComments,
    };
    setSelectedAction(newSelectedAction);
    dispatch(updateActionPlan({ accountId: activeAccount._id, entityId: selectedEntity._id, actions: newSelectedAction }));
  };

  const getUserInfo = () => {
    const user: UserProps = {
      name: currentUser.name ? currentUser.name : currentUser.email,
      id: currentUser._id,
      firstName: currentUser.firstName ?? '',
      email: currentUser.email,
    };
    return user;
  };

  const removeResponsibleFromAction = responsibleId => {
    let newResponsibleArray = selectedAction.responsible.filter(resp => resp.id !== responsibleId);
    if (newResponsibleArray.length === 0) {
      dispatch(
        setErrors({
          title: 'Avertissement',
          message:
            'Vous ne pouvez pas supprimer tous les utilisateurs attribués à cette action. Veuillez laisser au minimum un utilisateur en tant que responsable de cette action.',
        })
      );
      return;
    }
    setSelectedAction(prevState => ({ ...prevState, responsible: newResponsibleArray }));
  };

  return (
    <>
      {showDrawer && (
        <Drawer
          setOpen={() => {
            setShowDrawer(false);
            setSelectedAction({});
          }}
          open={showDrawer}
          width='66%'
          header={
            <TabsContainer>
              <Tab selected={'detail' === selectedItemID} onClick={() => setSelectedItemID('detail')}>
                <span>
                  <Badge>
                    <GrTask />
                  </Badge>
                  {selectedAction.category === 'Audit' ? "Détails de l'audit" : "Détails de l'action"}
                </span>
              </Tab>
              <Tab selected={'chat' === selectedItemID} onClick={() => setSelectedItemID('chat')}>
                <span>
                  <Badge>
                    <IoChatbubblesOutline size={24} />
                  </Badge>
                  {'Discussion'}
                </span>
              </Tab>
            </TabsContainer>
          }>
          <>
            {selectedItemID === 'chat' ? (
              <Drawer.Content>
                <Chat
                  deleteMessage={deleteComment}
                  isLoading={false}
                  messages={comments}
                  user={getUserInfo()}
                  handleMessage={updateCommentAction}
                  updateSeenStatus={updateSeenStatus}
                />
              </Drawer.Content>
            ) : (
              <>
                <Drawer.Content>
                  <DivContainer center bolder>
                    <span>Action</span>
                    {selectedAction.title}
                  </DivContainer>

                  {/* Task Description */}
                  <DivContainer>
                    <span>Description</span>
                    <div className='justify-text' dangerouslySetInnerHTML={{ __html: emphasizedText(selectedAction.description) }}></div>
                  </DivContainer>

                  {/* Task explanation */}
                  {selectedAction.explanation && (
                    <DivContainer>
                      <span>Explication d'intégration</span>
                      <div className='justify-text' dangerouslySetInnerHTML={{ __html: emphasizedText(selectedAction.explanation) }}></div>
                    </DivContainer>
                  )}

                  {/* Task Precision */}
                  {selectedAction.precision && (
                    <DivContainer>
                      <span>Précision</span>
                      <div className='justify-text' dangerouslySetInnerHTML={{ __html: emphasizedText(selectedAction.precision) }}></div>
                    </DivContainer>
                  )}

                  <Divider variant='middle' sx={{ margin: '16px' }} />

                  {/* Product Title */}
                  <DivContainer size='small' center height>
                    <span>Produit</span>
                    {selectedAction.isEntityScoop ? selectedEntity.name : selectedAction.versionProductName}
                  </DivContainer>

                  {/* Category */}
                  <DivContainer size='small' center height>
                    <span>Catégorie</span>
                    <Tag>{selectedAction.category}</Tag>
                  </DivContainer>

                  {/* Urgency */}
                  <DivContainer size='small' center height>
                    <span>Criticité</span>
                    <Tag urgency={selectedAction.urgency}>
                      {selectedAction.urgency === 'low' && <FcLowPriority fontSize={24} />}
                      {selectedAction.urgency === 'medium' && <FcMediumPriority fontSize={24} />}
                      {selectedAction.urgency === 'high' && <FcHighPriority fontSize={24} />}
                      {actionsUrgencyComplexity[selectedAction.urgency]}
                    </Tag>
                  </DivContainer>

                  {/* Status */}
                  <DivContainer size='small' center height>
                    <span>Status</span>
                    {admin || selectedAction.category === 'Audit' ? (
                      <ActionStatus status={selectedAction.status} justifyContent='inherit' />
                    ) : (
                      <CustomSelect
                        value={selectedAction.status}
                        onChange={(e: SelectChangeEvent) => handleStatusChange(e)}
                        IconComponent={props => <FaChevronUp {...props} inputprops={{ readOnly: admin }} />}>
                        <MenuItem value={'pending'}>En attente</MenuItem>
                        <MenuItem value={'ongoing'}>En cours</MenuItem>
                        <MenuItem value={'completed'}>Fait</MenuItem>
                      </CustomSelect>
                    )}
                  </DivContainer>

                  {/* SubContractor Contact */}
                  {selectedAction.category === 'Audit' && (
                    <DivContainer
                      size='small'
                      center
                      height
                      onClick={() => {
                        !selectedAction?.subContractor?.contactEmail && setShowSubcontractorInputModal(true);
                      }}>
                      <span>Contact Sous-Traitant</span>
                      {selectedAction.subContractor?.contactEmail || <span style={{ cursor: 'pointer' }}>Non renseigné</span>}
                    </DivContainer>
                  )}

                  {/* Date d'échéance  */}
                  <DivContainer size='small' center height>
                    <span>Date d'échéance</span>
                    {admin || selectedAction.category === 'Audit' ? (
                      selectedAction.dueDate ? (
                        dayjs(selectedAction.dueDate, 'DD-MM-YYYY').format('DD-MM-YYYY').toString()
                      ) : (
                        '-- / -- / ----'
                      )
                    ) : (
                      <div onClick={handleDateClick}>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <CustomDatePicker
                            editable
                            value={dayjs(selectedAction.dueDate, 'DD-MM-YYYY')}
                            onChange={date => {
                              handleDateChange(date);
                            }}
                            format={'DD-MM-YYYY'}
                            disablePast
                            slots={{
                              openPickerIcon: BsTable,
                              // popper: {
                              //   anchorOrigin: {
                              //     vertical: 'top',
                              //     horizontal: 'left',
                              //   },
                              //   transformOrigin: {
                              //     vertical: 'bottom',
                              //     horizontal: 'left',
                              //   },
                              //   position: 'bottom-start', // Use 'position' instead of 'placement'
                              // },
                            }}
                            ref={datePickerRef}
                            timezone={'Europe/Paris'}
                          />
                        </LocalizationProvider>
                      </div>
                    )}
                  </DivContainer>

                  {/* CreatedAt */}
                  <DivContainer size='small' center height>
                    <span>Date de Création</span>
                    {dayjs(selectedAction?.createdAt).format('DD-MM-YYYY')}
                  </DivContainer>

                  {/* Task Responsible */}
                  <DivContainer size='small'>
                    <span style={{ display: 'flex', alignItems: 'center', height: '40px' }}>Responsable de l'action</span>
                    <ResponsibleContainer>
                      {selectedAction?.responsible?.length ? (
                        selectedAction?.responsible?.map((responsible, index) => (
                          <ResponsibleItem key={index}>
                            <Avatar {...stringAvatar(getResponsibleName(responsible?.email).toUpperCase())} />
                            <span style={{ color: 'grey' }}>{responsible?.name || getResponsibleName(responsible?.email) || responsible?.email}</span>
                            {!admin && <MdClose onClick={() => removeResponsibleFromAction(responsible?.id)} />}
                          </ResponsibleItem>
                        ))
                      ) : (
                        <ResponsibleItem>
                          <span>Non attribué</span>
                        </ResponsibleItem>
                      )}
                    </ResponsibleContainer>
                  </DivContainer>
                </Drawer.Content>
                {!admin && selectedAction.category !== 'Audit' && (
                  <Drawer.Footer align='full'>
                    <Button
                      label='Confirmer'
                      onClick={e => {
                        e.preventDefault();
                        handleValidateEditedAction();
                      }}
                      disabled={!isActionChanged(selectedAction, editedAction)}
                    />
                    <Button
                      label='Annuler'
                      type='light'
                      onClick={e => {
                        e.preventDefault();
                        setShowDrawer(false);
                        setSelectedAction({});
                      }}
                    />
                  </Drawer.Footer>
                )}
              </>
            )}
          </>
        </Drawer>
      )}
    </>
  );
};

export default ActionDetails;
